import { trpc } from '@/utils/trpc';
import { collections } from '@prisma/client';
import { captureException } from '@sentry/nextjs';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import useNotification from '../../hooks/useNotification';
import { TCollection } from '../../interfaces/collections';
import { TPhoto } from '../../interfaces/photos';
import { colorManagerAtom } from '../../store/uiStore';
import Button from '../shared/Button';
import MediaViewer from '../shared/MediaViewer';
import Input from '../shared/molecules/Input';
import Textarea from '../shared/molecules/Textarea';

const EditMoodBoard = ({
  collection,
  newBoard,
  onSaved,
  onDelete,
  media,
  onClose,
}: {
  collection?: TCollection;
  newBoard?: boolean;
  onSaved?: (c: collections) => void;
  onDelete?: () => void;
  media?: TPhoto;
  onClose: () => void;
}) => {
  const [boardName, setBoardName] = useState('');
  const [description, setDescription] = useState('');
  const { showNotification } = useNotification();
  const [colorManager] = useAtom(colorManagerAtom);

  const { refetch: refetchCollections } = trpc.collections.getUserCollections.useQuery(undefined, {
    staleTime: Infinity,
  });
  const createUserBoardMutation = trpc.collections.createUserBoard.useMutation();
  const editUserBoardMutation = trpc.collections.editUserBoard.useMutation();
  const archiveUserBoardMutation = trpc.collections.archiveUserBoard.useMutation();

  useEffect(() => {
    if (collection) {
      if (collection.name) {
        setBoardName(collection.name);
      }
      if (collection.description) {
        setDescription(collection.description);
      }
    }
  }, [collection]);

  let res: collections[];
  const handleSave = async () => {
    if (boardName.length > 15) {
      return;
    }
    try {
      if (!newBoard && collection) {
        res = await editUserBoardMutation.mutateAsync({
          id: Number(collection.id),
          name: boardName,
          description,
        });

        refetchCollections();
      }
      if (newBoard) {
        res = await createUserBoardMutation.mutateAsync({
          name: boardName,
          description,
          media,
        });

        refetchCollections();
      }

      if (onSaved) {
        const matchName = res.filter((col) => col.name === boardName);
        const highestId = Math.max(...matchName.map((o) => Number(o.id)), 0);
        const newCol = matchName.find((col) => Number(col.id) === highestId);

        if (newCol) onSaved(newCol);
      }
    } catch (error) {
      showNotification({ message: `Unable to save board: ${error.message}` }, 6000);
      captureException(error);
    }
  };

  const handleArchive = async () => {
    if (!collection) return;

    try {
      await archiveUserBoardMutation.mutateAsync(Number(collection.id));
      refetchCollections();

      showNotification({ message: 'Board Deleted' });
      if (onDelete) onDelete();
    } catch (error) {
      showNotification({ message: 'Unable to delete board' });
      captureException(error);
    }
  };

  const handleSetBoardName = (name) => {
    if (name.length > 15) {
      return;
    }
    setBoardName(name);
  };

  return (
    <div
      className={clsx([
        'p-[32px] grid content-start',
        media ? 'grid-cols-[307px_1fr] p-[60px] gap-[70px]' : 'grid-cols-[411px_1fr]',
        colorManager.text === 'text-black' ? 'border-black' : 'border-white',
        colorManager.background,
      ])}
    >
      {media && (
        <div className="grid">
          <MediaViewer
            media={media}
            showBookmarkButton={false}
            showQuickDownload={false}
            allowSingleModalView={media?.type === 'clip' ? true : false}
            cover={true}
            videoAutoPlay={true}
          />
        </div>
      )}

      <div
        className={clsx([
          'grid content-start grid-cols-[411px]',
          media ? 'gap-[32px]' : 'gap-[20px]',
        ])}
      >
        <div className="absolute right-[32px]">
          <svg
            onClick={onClose}
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="cursor-pointer"
          >
            <path
              d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"
              fill={colorManager.text === 'text-black' ? '#000' : '#fff'}
            />
          </svg>
        </div>
        <div>
          <Input
            label="Board name"
            type="text"
            value={boardName}
            onChange={(e) => handleSetBoardName(e.target.value)}
            backgroundColor={colorManager.background}
            textColor={colorManager.text}
            inputClass={colorManager.text === 'text-black' ? '!border-black border' : ''}
            labelGap={10}
            autoFocus
          />
          <div className="flex justify-between mt-2.5">
            <p className="text-[12px] tracking-[0.03em]">({boardName.length}/15 Characters)</p>

            {/* // TODO character limit */}
          </div>
        </div>
        <div className="grid content-start grid-rows-[min-content_min-content_1fr]">
          <div className="flex">
            <p className="font-normal font-adieu text-sm leading-none tracking-[0.15em] uppercase 3xl:text-sm 4xl:text-[15px] pb-[10px]">
              Description
            </p>
            <p className="text-[12px] tracking-[0.03em] ml-[8px]">(Optional)</p>
          </div>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={8}
            textColor={colorManager.text}
            className={colorManager.text === 'text-black' ? '!border-black border' : ''}
          />

          <div
            className={`flex items-end mt-[30px] ${
              newBoard ? 'justify-center' : 'justify-between'
            }`}
          >
            {!newBoard && (
              <Button
                type="secondary"
                onClick={handleArchive}
                className={`${colorManager.text} ${
                  colorManager.text === 'text-black' ? 'border-black' : ''
                }`}
              >
                Delete
              </Button>
            )}
            <Button onClick={handleSave} disabled={boardName.length === 0}>
              {newBoard ? 'Create' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditMoodBoard;
